import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import type { Notification } from '../../types/notification';

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onClose: () => void;
}

export function NotificationItem({ notification, onMarkRead, onClose }: NotificationItemProps) {
  const getEntityLink = (): string | null => {
    if (notification.bewerberId) {
      return `/bewerber/${notification.bewerberId}`;
    }
    if (notification.bedarfId) {
      return `/bedarfe/${notification.bedarfId}`;
    }
    return null;
  };

  const getEntityName = (): string => {
    if (notification.bewerber) {
      return `${notification.bewerber.firstName} ${notification.bewerber.lastName}`;
    }
    if (notification.bedarf) {
      return `${notification.bedarf.childFirstName}${notification.bedarf.childLastName ? ' ' + notification.bedarf.childLastName : ''}`;
    }
    return '';
  };

  const handleClick = () => {
    if (!notification.isRead) {
      onMarkRead(notification.id);
    }
    onClose();
  };

  const link = getEntityLink();
  const entityName = getEntityName();
  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
    locale: de,
  });

  const content = (
    <div
      className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
        !notification.isRead ? 'bg-primary-50' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
          <span className="text-sm font-medium text-primary-700">
            {notification.triggeredBy.firstName[0]}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 font-medium">
            {notification.title}
          </p>
          {entityName && (
            <p className="text-xs text-gray-500 mt-0.5">
              {notification.bewerberId ? 'Bewerber' : 'Bedarf'}: {entityName}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>
        </div>

        {/* Unread indicator */}
        {!notification.isRead && (
          <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500" />
        )}
      </div>
    </div>
  );

  if (link) {
    return (
      <Link to={link} onClick={handleClick} className="block">
        {content}
      </Link>
    );
  }

  return <div onClick={handleClick}>{content}</div>;
}
